/**
 * §5.1-bug4 — tenant status client.
 *
 * Single thin hook around `/api/admin/tenant-status`. Used by the
 * OnboardingWizard redirect guard and (later) the AppShell sidebar
 * to hide the Onboarding link for already-provisioned tenants.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { api, ApiError } from "./api";
import { isAuthenticated } from "./auth";

export interface TenantStatus {
  tenant_id: string;
  provisioned_at: number | null;
  auth_mode: string;
  // §v3.sidebar-polish (2026-05-01) — used by AppShell to hide
  // retail-only nav items (Orders / Products / Customers) for
  // school-receptionist tenants. Optional for backward compat with
  // older backends; treat absence as "show everything" (current
  // Roze behaviour).
  persona_category?: string;
  // §industry-aware-dash B1 (2026-08-08) — the tenant's resolved industry
  // PACK key ("retail" | "clinic" | "hospitality" | "real_estate" |
  // "financial_services"), or null when no pack resolves. THE signal the
  // DashboardConfig registry keys on — never the tenant id string, which is
  // how the per-tenant forks grew. Absent/null → the neutral default
  // config, never another trade's.
  vertical?: string | null;
  // §dashboard-first-run (2026-08-09) — the tenant's ISO currency code,
  // resolved server-side (payment_currency → currency brand-fact → country
  // ladder → AED). Display code for money surfaces that used to hardcode
  // "AED"; absent (older backend) → callers fall back to AED, which is what
  // the legacy tenants resolve to anyway.
  currency?: string | null;
}

export async function getTenantStatus(): Promise<TenantStatus> {
  return api<TenantStatus>("/api/admin/tenant-status");
}

/**
 * React Query hook. Disabled while the operator is unauthenticated so
 * the wizard's cold-signup path (no JWT yet) doesn't fire a 401.
 *
 * §v3.poll-tuning (2026-05-06) — explicit ``refetchInterval: 5min``
 * override. Without it, the global 5 s polling default in App.tsx
 * applied here too, which meant every page hit ``/api/admin/tenant-
 * status`` 12 times a minute even though the value flips once per
 * tenant lifetime (at Go Live). ``staleTime`` already signalled the
 * intent; React Query needs the polling cadence to match it
 * separately.
 */
export function useTenantStatus(): UseQueryResult<TenantStatus, ApiError> {
  return useQuery<TenantStatus, ApiError>({
    queryKey: ["tenant-status"],
    queryFn: getTenantStatus,
    enabled: isAuthenticated(),
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
